import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Smartphone, 
  Download, 
  Share, 
  Home, 
  CheckCircle,
  X,
  Monitor,
  Tablet
} from 'lucide-react';
import { usePWAManager } from './PWAManager';

// Storage keys for dismissal state
const STORAGE_KEYS = {
  PWA_PROMPT_DISMISSED: 'pwa_install_prompt_dismissed',
  DISMISSAL_TIMESTAMP: 'pwa_dismissal_timestamp'
};

// Device Detection Hook
const useDeviceDetection = () => {
  const [deviceInfo, setDeviceInfo] = React.useState({
    isDesktop: false,
    isAndroid: false,
    isTablet: false,
    browser: 'unknown'
  });

  React.useEffect(() => {
    const userAgent = navigator.userAgent;
    
    const isAndroid = /Android/i.test(userAgent);
    const isTablet = /iPad|Android.*Mobile/i.test(userAgent) && window.innerWidth > 768;
    const isDesktop = !(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent));
    
    let browser = 'unknown';
    if (userAgent.includes('Chrome') && !userAgent.includes('Edge')) browser = 'chrome';
    else if (userAgent.includes('Edge')) browser = 'edge';
    else if (userAgent.includes('Firefox')) browser = 'firefox';
    else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'safari';

    setDeviceInfo({ isDesktop, isAndroid, isTablet, browser });
  }, []);

  return deviceInfo;
};

// Hook to manage persistent dismissal state
const usePersistentDismissal = () => {
  const [isDismissed, setIsDismissed] = React.useState(false);

  React.useEffect(() => {
    // Check if previously dismissed
    const dismissed = localStorage.getItem(STORAGE_KEYS.PWA_PROMPT_DISMISSED) === 'true';
    setIsDismissed(dismissed);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEYS.PWA_PROMPT_DISMISSED, 'true');
    localStorage.setItem(STORAGE_KEYS.DISMISSAL_TIMESTAMP, new Date().toISOString());
    setIsDismissed(true);
  };

  const reset = () => {
    localStorage.removeItem(STORAGE_KEYS.PWA_PROMPT_DISMISSED);
    localStorage.removeItem(STORAGE_KEYS.DISMISSAL_TIMESTAMP);
    setIsDismissed(false);
  };

  const getDismissalInfo = () => {
    const timestamp = localStorage.getItem(STORAGE_KEYS.DISMISSAL_TIMESTAMP);
    return timestamp ? new Date(timestamp) : null;
  };

  return { isDismissed, dismiss, reset, getDismissalInfo };
};

interface PWAInstallPromptProps {
  onDismiss?: () => void;
  showDismiss?: boolean;
}

export const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ 
  onDismiss, 
  showDismiss = true 
}) => {
  const { pwaStatus, showInstallPrompt, promptInstall } = usePWAManager();
  const deviceInfo = useDeviceDetection();
  const { isDismissed, dismiss } = usePersistentDismissal();
  const [showInstructions, setShowInstructions] = React.useState(false);

  // Don't show if no PWA status or previously dismissed
  if (!pwaStatus || isDismissed) return null;

  const handleDismiss = () => {
    dismiss(); // Store dismissal permanently
    onDismiss?.();
  };

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      dismiss(); // Don't show again if successfully installed
    }
  };

  // ✅ SUCCESS STATE: Already PWA - notifications handled automatically
  if (pwaStatus.isPWA) {
    const notificationsEnabled = localStorage.getItem('notifications_registered') === 'true';
    
    return (
      <Alert className="border-green-200 bg-green-50">
        <CheckCircle className="h-4 w-4" />
        <AlertTitle className="flex items-center justify-between">
          App Ready!
          {showDismiss && (
            <Button variant="ghost" size="sm" onClick={handleDismiss}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </AlertTitle>
        <AlertDescription>
          <p>
            ✅ App is installed and ready to use.
            {notificationsEnabled && " You'll receive important work notifications."}
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  // Get device-specific instructions
  const getInstallInstructions = () => {
    if (deviceInfo.isAndroid) {
      return {
        icon: <Smartphone className="h-4 w-4 mr-2" />,
        title: "Android Installation:",
        steps: [
          "Tap the menu (⋮) in Chrome",
          "Select \"Add to Home screen\"", 
          "Tap \"Add\" to confirm",
          "Open the app from your home screen"
        ]
      };
    } else if (deviceInfo.isTablet) {
      return {
        icon: <Tablet className="h-4 w-4 mr-2" />,
        title: "Tablet Installation:",
        steps: [
          "Look for the install button in your browser's address bar",
          "Or use your browser menu → \"Add to Home screen\"",
          "Confirm the installation"
        ]
      };
    } else if (deviceInfo.isDesktop) {
      const browserSpecific = deviceInfo.browser === 'chrome' || deviceInfo.browser === 'edge';
      return {
        icon: <Monitor className="h-4 w-4 mr-2" />,
        title: browserSpecific ? `${deviceInfo.browser === 'chrome' ? 'Chrome' : 'Edge'} Installation:` : "Desktop Installation:",
        steps: browserSpecific ? [
          "Look for the ⬇️ install button in the address bar",
          "Or click the menu (⋮) → \"Install Staffluent...\"",
          "Click \"Install\" in the dialog"
        ] : [
          "Try using Chrome or Edge for app installation",
          "You can bookmark this page for quick access"
        ]
      };
    }
    return null;
  };

  const instructions = getInstallInstructions();

  return (
    <div className="space-y-4">
      {/* iOS PWA Installation Required */}
      {pwaStatus.isIOS && !pwaStatus.isPWA && (
        <Alert className="border-orange-200 bg-orange-50">
          <Smartphone className="h-4 w-4" />
          <AlertTitle className="flex items-center justify-between">
            Install App for Best Experience
            {showDismiss && (
              <Button variant="ghost" size="sm" onClick={handleDismiss}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </AlertTitle>
          <AlertDescription>
            <div className="space-y-3">
              <p>Install this app to your home screen for notifications and better performance:</p>
              
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center text-xs">1</Badge>
                  <span>Tap the <Share className="inline h-3 w-3 mx-1" /> Share button below</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center text-xs">2</Badge>
                  <span>Select "Add to Home Screen"</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center text-xs">3</Badge>
                  <span>Open the app from your <Home className="inline h-3 w-3 mx-1" /> home screen</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center text-xs">4</Badge>
                  <span>Allow notifications when prompted</span>
                </div>
              </div>

              {pwaStatus.iosVersion < 16.4 && (
                <div className="p-3 bg-red-50 rounded border border-red-200 mt-3">
                  <p className="text-red-600 text-sm">⚠️ iOS 16.4+ required for notifications. Please update your device.</p>
                </div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Automatic Install Prompt Available */}
      {!pwaStatus.isIOS && showInstallPrompt && !pwaStatus.isPWA && (
        <Alert className="border-blue-200 bg-blue-50">
          <Download className="h-4 w-4" />
          <AlertTitle className="flex items-center justify-between">
            Install App
            {showDismiss && (
              <Button variant="ghost" size="sm" onClick={handleDismiss}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </AlertTitle>
          <AlertDescription>
            <div className="space-y-3">
              <p>Install this app for a better experience and notifications.</p>
              <div className="flex space-x-2">
                <Button onClick={handleInstall} size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Install App
                </Button>
                {showDismiss && (
                  <Button variant="outline" size="sm" onClick={handleDismiss}>
                    Maybe Later
                  </Button>
                )}
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Manual Install Instructions */}
      {!pwaStatus.isIOS && !showInstallPrompt && !pwaStatus.isPWA && instructions && (
        <Alert className="border-blue-200 bg-blue-50">
          {instructions.icon}
          <AlertTitle className="flex items-center justify-between">
            Install App for Best Experience
            {showDismiss && (
              <Button variant="ghost" size="sm" onClick={handleDismiss}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </AlertTitle>
          <AlertDescription>
            <div className="space-y-3">
              <p>Install this app for notifications and offline access.</p>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowInstructions(!showInstructions)}
                className="w-full"
              >
                {showInstructions ? 'Hide' : 'Show'} Install Instructions
              </Button>

              {showInstructions && (
                <div className="p-3 bg-white rounded border">
                  <p className="font-medium mb-2 flex items-center">
                    {instructions.icon}
                    {instructions.title}
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    {instructions.steps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

// ✅ UTILITY COMPONENT: For testing/debugging
export const PWAPromptDebugPanel: React.FC = () => {
  const { reset, getDismissalInfo } = usePersistentDismissal();
  const dismissalDate = getDismissalInfo();

  return (
    <div className="p-4 border rounded bg-gray-50">
      <h3 className="font-medium mb-2">PWA Prompt Debug Panel</h3>
      <div className="space-y-2 text-sm">
        <p>
          <strong>Status:</strong> {dismissalDate ? 'Dismissed' : 'Active'}
        </p>
        {dismissalDate && (
          <p>
            <strong>Dismissed on:</strong> {dismissalDate.toLocaleString()}
          </p>
        )}
        <Button size="sm" onClick={reset} variant="outline">
          Reset Dismissal (Show Prompt Again)
        </Button>
      </div>
    </div>
  );
};